import React, { useState, useEffect } from "react";
import {
    Card,
    CardHeader,
    CardBody,
    Container,
    Row,
    Col,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
} from "reactstrap";
import CommonHeader from "components/Headers/CommonHeader";
import Swal from "sweetalert2";
import { getContact, saveContact } from "api/ApiServices";

const ContactUs = () => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [contactData, setContactData] = useState({
        name: "",
        email: "",
        phone: "",
        country_code: "",
        address: ""
    });

    const [errors, setErrors] = useState({
        name: false,
        email: false,
        phone: false,
        address: false,
        country_code: false,
    });

    useEffect(() => {
        fetchContact();
    }, []);

    const fetchContact = async () => {
        setLoading(true);
        try {
            const res = await getContact();
            if (res.status && res.payload?.contact) {
                const data = res.payload.contact;
                setContactData({
                    name: data.c_name || "",
                    email: data.c_email || "",
                    phone: data.c_phone || "",
                    country_code: data.c_country_code || "",
                    address: data.c_address || ""
                });
            }
        } catch (error) {
            console.error("Error fetching contact details:", error);
            Swal.fire("Error", "Could not load contact information.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();

        const newErrors = {
            name: !contactData.name?.trim(),
            email: !contactData.email?.trim(),
            phone: !contactData.phone?.toString().trim(),
            country_code: !contactData.country_code?.trim(),
            address: !contactData.address?.trim(),
        };

        setErrors(newErrors);

        const hasErrors = Object.values(newErrors).some((error) => error);
        if (hasErrors) {
            Swal.fire({
                title: "Validation Error!",
                text: "Please fill all required fields correctly.",
                icon: "error",
                confirmButtonText: "OK",
            });
            return;
        }

        setSaving(true);
        try {
            const res = await saveContact(contactData);
            if (res.status) {
                Swal.fire({
                    title: "Contact Updated!",
                    text: "Support configuration has been globally updated.",
                    icon: "success",
                    timer: 2000,
                    showConfirmButton: false,
                }).then(() => {
                    fetchContact();
                });
            } else {
                Swal.fire("Error", res.message || "Failed to update contact.", "error");
            }
        } catch (error) {
            console.error("Error saving contact:", error);
            Swal.fire("Error", "Something went wrong while saving contact details.", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <CommonHeader />
            <Container className="mt--7" fluid>
                <Row>
                    {/* LEFT PANEL: Summary Card */}
                    <Col className="order-xl-2 mb-5 mb-xl-0" xl="4">
                        <Card className="card-profile shadow border-0">
                            <Row className="justify-content-center">
                                <Col className="order-lg-2" lg="3">
                                    <div className="card-profile-image" style={{ textAlign: "center" }}>
                                        <div
                                            className="rounded-circle shadow-lg d-flex align-items-center justify-content-center bg-gradient-success pt-4"
                                            style={{
                                                width: "120px",
                                                height: "120px",
                                                border: "4px solid #fff",
                                                display: "block",
                                                margin: "0 auto",
                                            }}
                                        >
                                            <i className="ni ni-mobile-button text-white display-3" />
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                            <CardHeader className="text-center border-0 pt-8 pt-md-4 pb-0 pb-md-4"></CardHeader>
                            <CardBody className="pt-0 pt-md-4">
                                <div className="text-center mt-7">
                                    <h3>
                                        {contactData.name || "Global Support"}
                                    </h3>
                                    <div className="h5 font-weight-300">
                                        <i className="ni location_pin mr-2" />
                                        {contactData.address || "Main Headquarters"}
                                    </div>
                                    <div className="h5 mt-4">
                                        <i className="ni business_briefcase-24 mr-2" />
                                        {contactData.email || "support@cradleai.com"}
                                    </div>
                                    <div>
                                        <i className="ni ni-favourite-28 mr-2" />
                                        App-Wide Public Directory
                                    </div>
                                    <hr className="my-4" />
                                    <p>
                                        These details are visible to normal users across the global app platform as official contact details.
                                    </p>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>

                    {/* RIGHT PANEL: Form Card */}
                    <Col className="order-xl-1" xl="8">
                        <Card className="bg-secondary shadow border-0">
                            <CardHeader className="bg-white border-0">
                                <Row className="align-items-center">
                                    <Col xs="8">
                                        <h3 className="mb-0">
                                            <i className="ni ni-headphones text-success mr-2" /> Edit Contact Us
                                        </h3>
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody>
                                {loading ? (
                                    <div className="text-center py-5">
                                        <i className="fas fa-spinner fa-spin fa-2x text-primary" />
                                        <p className="mt-3 text-muted">Loading...</p>
                                    </div>
                                ) : (
                                    <Form>
                                        <h6 className="heading-small text-muted mb-4">
                                            Public Support Identity
                                        </h6>
                                        <div className="pl-lg-4">
                                            <Row>
                                                <Col lg="6">
                                                    <FormGroup>
                                                        <label className="form-control-label" htmlFor="input-name">
                                                            Support Title <span style={{ color: "red" }}>*</span>
                                                        </label>
                                                        <Input
                                                            className={`form-control-alternative ${errors.name ? "border-danger" : ""}`}
                                                            id="input-name"
                                                            placeholder="e.g. CradleAI Help Desk"
                                                            type="text"
                                                            value={contactData.name}
                                                            onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
                                                            style={{ color: "#32325d", fontWeight: "500" }}
                                                        />
                                                        {errors.name && <small className="text-danger">Support Title is required.</small>}
                                                    </FormGroup>
                                                </Col>
                                                <Col lg="6">
                                                    <FormGroup>
                                                        <label className="form-control-label" htmlFor="input-email">
                                                            Public Email <span style={{ color: "red" }}>*</span>
                                                        </label>
                                                        <Input
                                                            className={`form-control-alternative ${errors.email ? "border-danger" : ""}`}
                                                            id="input-email"
                                                            placeholder="support@domain.com"
                                                            type="email"
                                                            value={contactData.email}
                                                            onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                                                            style={{ color: "#32325d", fontWeight: "500" }}
                                                        />
                                                        {errors.email && <small className="text-danger">Email is required.</small>}
                                                    </FormGroup>
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col lg="4">
                                                    <FormGroup>
                                                        <label className="form-control-label" htmlFor="input-country-code">
                                                            Country Code <span style={{ color: "red" }}>*</span>
                                                        </label>
                                                        <Input
                                                            className={`form-control-alternative ${errors.country_code ? "border-danger" : ""}`}
                                                            id="input-country-code"
                                                            placeholder="+1"
                                                            type="text"
                                                            value={contactData.country_code}
                                                            onChange={(e) => setContactData({ ...contactData, country_code: e.target.value })}
                                                            style={{ color: "#32325d", fontWeight: "500" }}
                                                        />
                                                        {errors.country_code && <small className="text-danger">Code required.</small>}
                                                    </FormGroup>
                                                </Col>
                                                <Col lg="8">
                                                    <FormGroup>
                                                        <label className="form-control-label" htmlFor="input-phone">
                                                            Phone Number <span style={{ color: "red" }}>*</span>
                                                        </label>
                                                        <Input
                                                            className={`form-control-alternative ${errors.phone ? "border-danger" : ""}`}
                                                            id="input-phone"
                                                            placeholder="555-0198"
                                                            type="text"
                                                            value={contactData.phone}
                                                            onChange={(e) => {
                                                                const onlyNums = e.target.value.replace(/[^0-9]/g, "");
                                                                setContactData({ ...contactData, phone: onlyNums });
                                                            }}
                                                            style={{ color: "#32325d", fontWeight: "500" }}
                                                        />
                                                        {errors.phone && <small className="text-danger">Phone Number is required.</small>}
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>
                                        <hr className="my-4" />

                                        <h6 className="heading-small text-muted mb-4">
                                            Location Information
                                        </h6>
                                        <div className="pl-lg-4">
                                            <Row>
                                                <Col md="12">
                                                    <FormGroup>
                                                        <label className="form-control-label" htmlFor="input-address">
                                                            Physical Address <span style={{ color: "red" }}>*</span>
                                                        </label>
                                                        <Input
                                                            className={`form-control-alternative ${errors.address ? "border-danger" : ""}`}
                                                            id="input-address"
                                                            placeholder="Headquarters Address"
                                                            type="textarea"
                                                            rows="4"
                                                            value={contactData.address}
                                                            onChange={(e) => setContactData({ ...contactData, address: e.target.value })}
                                                            style={{ color: "#32325d", fontWeight: "500" }}
                                                        />
                                                        {errors.address && <small className="text-danger">Address is required.</small>}
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                        </div>

                                        <div className="ms-2 pl-lg-4 mt-2 mb-3">
                                            <Button
                                                color="success"
                                                href="#pablo"
                                                onClick={handleUpdate}
                                                disabled={saving}
                                            >
                                                {saving ? <i className="fas fa-spinner fa-spin mr-2" /> : "Update Details"}
                                            </Button>
                                        </div>
                                    </Form>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default ContactUs;
